<template>
  <div class="fields-matcher">
    <p class="matcher-hint">
      Map each required field to a column from your file. Optional fields can be
      left as "Not provided".
    </p>
    <div class="preview-toggle">
      <label>
        <input type="checkbox" v-model="showFirstRow" />
        Show sample values from first row
      </label>
    </div>
    <div class="match-table">
      <div class="match-header">
        <span>Required field</span>
        <span>Column in your file</span>
        <span v-if="showFirstRow">Sample value</span>
      </div>
      <div
        v-for="field in mapping"
        :key="field.name"
        class="match-row"
        :class="{ 'is-required': field.required, 'has-error': field.required && !field.matchField }"
      >
        <span class="field-name">
          {{ field.label || field.name }}
          <span v-if="field.required" class="required-mark">*</span>
        </span>
        <select class="default-select" v-model="field.matchField">
          <option :value="null">— Not provided —</option>
          <option
            v-for="col in uploadedFields"
            :key="col"
            :value="col"
          >
            {{ col }}
          </option>
        </select>
        <span v-if="showFirstRow" class="sample-value">
          {{ field.matchField ? firstRowDecoded[field.matchField] : '–' }}
        </span>
      </div>
    </div>
    <p v-if="missingRequired.length" class="error-hint">
      Required fields missing: {{ missingRequired.join(', ') }}
    </p>
    <button
      id="confirm-btn"
      class="btn-dark-button"
      @click="confirmMatch()"
      :disabled="missingRequired.length > 0"
    >
      Confirm and process file
    </button>
  </div>
</template>
<script>
export default {
  props: {
    uploadedFields: {
      type: Array,
      required: true
    },
    requiredFields: {
      type: Array,
      required: true
    },
    firstRow: {
      type: String,
      required: true
    }
  },
  data() {
    const firstRowDecoded = JSON.parse(this.firstRow);
    // Strip anything that isn't a letter/number so "codigo_subtipo" and
    // "SUBTIPO" (or "Subtipo Code") normalize to comparable keys.
    const normalize = str => str.toLowerCase().replace(/[^a-z0-9]/g, "");
    const mapping = this.requiredFields.map(field => {
      const nameKey = normalize(field.name);
      // "codigo_subtipo" -> "subtipo": drop common prefixes so columns like
      // "SUBTIPO" auto-match fields whose real-world name is just the suffix.
      const shortKey = normalize(field.name.replace(/^codigo_/, ""));
      const labelKey = field.label ? normalize(field.label) : "";
      const autoMatch = this.uploadedFields.find(col => {
        const colKey = normalize(col);
        return (
          colKey === nameKey ||
          colKey === shortKey ||
          colKey === labelKey ||
          colKey.includes(shortKey) ||
          shortKey.includes(colKey)
        );
      });
      return {
        ...field,
        matchField: autoMatch || null
      };
    });
    return {
      mapping,
      firstRowDecoded,
      showFirstRow: false
    };
  },
  computed: {
    missingRequired() {
      return this.mapping
        .filter(f => f.required && !f.matchField)
        .map(f => f.label || f.name);
    }
  },
  methods: {
    confirmMatch() {
      if (this.missingRequired.length) return;
      const result = this.mapping.map(f => ({
        ...f,
        matchField: f.matchField || null
      }));
      this.$emit("confirmed", result);
    }
  }
};
</script>
<style lang="scss">
.fields-matcher {
  width: 100%;

  .matcher-hint {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
  }

  .preview-toggle {
    margin-bottom: 12px;
    font-size: 13px;
    label {
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
    }
  }

  .match-table {
    width: 100%;
    border-collapse: collapse;

    .match-header {
      display: grid;
      grid-template-columns: 1fr 1fr auto;
      font-weight: 600;
      font-size: 13px;
      padding: 6px 10px;
      border-bottom: 2px solid #ddd;
      margin-bottom: 4px;
    }

    .match-row {
      display: grid;
      grid-template-columns: 1fr 1fr auto;
      align-items: center;
      padding: 6px 10px;
      border: 1px solid #e0e0e0;
      margin-bottom: 4px;
      border-radius: 4px;
      gap: 10px;

      &.has-error {
        border-color: #e74c3c;
        background: #fff5f5;
      }

      .field-name {
        font-size: 14px;
        .required-mark {
          color: #e74c3c;
          margin-left: 2px;
        }
      }

      .sample-value {
        font-size: 12px;
        color: #888;
        min-width: 80px;
        text-align: right;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 120px;
      }

      select.default-select {
        width: 100%;
      }
    }
  }

  .error-hint {
    color: #e74c3c;
    font-size: 13px;
    margin: 8px 0;
  }

  #confirm-btn {
    margin-top: 16px;
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
